import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import embed from 'vega-embed';
import { expressionInterpreter } from 'vega-interpreter';
import styled, { keyframes } from 'styled-components';
import { colors } from '~/styles';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const S = {
  Container: styled.div`
    margin: 0.75rem 0 0.25rem;
    padding: 1rem;
    background: ${colors.white};
    border: 1px solid ${colors.grey10};
    border-radius: 0.5rem;
    width: 100%;
    min-height: 340px;
    position: relative;
    animation: ${fadeIn} 0.3s ease-out;

    .vega-embed {
      width: 100%;
    }

    .vega-embed > svg {
      width: 100%;
      height: auto;
    }
  `,

  Error: styled.div`
    margin: 0.75rem 0;
    padding: 1rem;
    background: ${colors.danger5};
    border: 1px solid ${colors.danger25};
    border-radius: 0.5rem;
    color: ${colors.danger};
    font-size: 0.875rem;
  `,

  ButtonRow: styled.div`
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.375rem 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  `,

  ActionButton: styled.button`
    border: 1px solid ${colors.grey10};
    border-radius: 999rem;
    background: ${colors.white};
    color: ${colors.grey75};
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
      background: ${colors.grey5};
      color: ${colors.black};
    }
  `,
};

const chartColors = [
  colors.teal,
  colors.green,
  colors.yellow,
  colors.red,
  colors.grey55,
  colors.accent,
  colors.primary50,
  colors.success50,
  colors.warning50,
  colors.danger50,
];

const semanticColorMap = {
  primary: colors.primary,
  primary50: colors.primary50,
  primary25: colors.primary25,
  success: colors.success,
  warning: colors.warning,
  danger: colors.danger,
  neutral: colors.grey55,
  accent: colors.accent,
};

const fontFamily = "'Roboto', sans-serif";

const defaultConfig = {
  background: colors.white,
  view: { stroke: 'transparent' },
  font: fontFamily,
  mark: { color: colors.teal },
  axis: {
    labelColor: colors.grey75,
    labelFont: fontFamily,
    labelFontSize: 12,
    titleColor: colors.grey75,
    titleFont: fontFamily,
    titleFontSize: 13,
    gridColor: colors.grey10,
    domainColor: colors.grey20,
    tickColor: colors.grey20,
  },
  legend: {
    labelColor: colors.grey75,
    labelFont: fontFamily,
    labelFontSize: 12,
    titleColor: colors.grey75,
    titleFont: fontFamily,
    titleFontSize: 13,
  },
  title: {
    color: colors.grey75,
    font: fontFamily,
    fontSize: 14,
    offset: 20,
  },
  range: {
    category: chartColors,
  },
};

function getAxisFormat(fields, fieldKey) {
  const field = fields.find((f) => f.key === fieldKey);
  if (!field?.semantic) return undefined;
  switch (field.semantic) {
    case 'currency':
      return '$,.2f';
    case 'hours':
      return ',.1f';
    case 'percent':
      return '.1%';
    default:
      return undefined;
  }
}

function resolveColorScale(colorInput, dimensionLabel, data, fields) {
  if (!colorInput) return null;

  // Array of tokens: map positionally to unique dimension values
  if (Array.isArray(colorInput)) {
    const dimField = fields.find((f) => f.key === dimensionLabel || f.label === dimensionLabel);
    const key = dimField?.key || dimensionLabel;
    const uniqueValues = [...new Set(data.map((row) => row[key]).filter((v) => v != null && v !== ''))];
    const domain = uniqueValues;
    const range = domain.map((_, i) => semanticColorMap[colorInput[i]] || chartColors[i % chartColors.length]);
    return {
      field: dimensionLabel,
      type: 'nominal',
      scale: { domain, range, unknown: colors.grey25 },
      legend: null,
    };
  }

  // Object map: { "Category": "primary", ... }
  // Validate that keys match actual dimension values; ignore if they don't (e.g. metric labels)
  if (typeof colorInput === 'object' && Object.keys(colorInput).length > 0) {
    const dimField = fields.find((f) => f.key === dimensionLabel || f.label === dimensionLabel);
    const key = dimField?.key || dimensionLabel;
    const uniqueValues = new Set(data.map((row) => row[key]).filter((v) => v != null));
    const domain = Object.keys(colorInput).filter((k) => uniqueValues.has(k));
    if (domain.length === 0) return null;
    const range = domain.map((k) => semanticColorMap[colorInput[k]] || chartColors[0]);
    return {
      field: dimensionLabel,
      type: 'nominal',
      scale: { domain, range, unknown: colors.grey25 },
      legend: null,
    };
  }

  return null;
}

function mapData(data, fields) {
  return data.map((row) => {
    const mapped = {};
    for (const field of fields) {
      const value = row[field.key];
      // Replace null/empty dimension values with "None" so Vega doesn't surface
      // the literal text "null" in axes, tooltips, or stacked-bar legends.
      // Metric fields keep their nulls so charts render gaps rather than zero.
      if (field.role !== 'metric' && (value == null || value === '')) {
        mapped[field.label] = 'None';
      } else {
        mapped[field.label] = value;
      }
    }
    return mapped;
  });
}

function getLabel(fields, key) {
  return fields.find((f) => f.key === key)?.label || key;
}

function getFieldType(fields, key) {
  return fields.find((f) => f.key === key)?.type || 'string';
}

function buildBarSpec(data, fields, dimension, metric, colorMap) {
  const dimLabel = getLabel(fields, dimension);
  const metLabel = getLabel(fields, metric);
  const metFormat = getAxisFormat(fields, metric);
  const colorEncoding = resolveColorScale(colorMap, dimLabel, data, fields);

  return {
    data: { values: mapData(data, fields) },
    mark: 'bar',
    encoding: {
      x: { field: dimLabel, type: 'nominal', title: dimLabel, axis: { labelAngle: -45 } },
      y: {
        field: metLabel,
        type: 'quantitative',
        title: metLabel,
        ...(metFormat ? { axis: { format: metFormat } } : {}),
      },
      ...(colorEncoding ? { color: colorEncoding } : {}),
      tooltip: [
        { field: dimLabel, type: 'nominal' },
        { field: metLabel, type: 'quantitative', ...(metFormat ? { format: metFormat } : {}) },
      ],
    },
  };
}

function buildLineSpec(data, fields, dimension, metric, series, colorMap) {
  const dimLabel = getLabel(fields, dimension);
  const metLabel = getLabel(fields, metric);
  const metFormat = getAxisFormat(fields, metric);
  const dimType = getFieldType(fields, dimension);
  const xType = dimType === 'date' ? 'temporal' : 'ordinal';

  const encoding = {
    x: { field: dimLabel, type: xType, title: dimLabel },
    y: {
      field: metLabel,
      type: 'quantitative',
      title: metLabel,
      ...(metFormat ? { axis: { format: metFormat } } : {}),
    },
    tooltip: [
      { field: dimLabel, type: xType },
      { field: metLabel, type: 'quantitative', ...(metFormat ? { format: metFormat } : {}) },
    ],
  };

  if (series) {
    const serLabel = getLabel(fields, series);
    const colorEncoding = resolveColorScale(colorMap, serLabel, data, fields);
    encoding.color = colorEncoding || { field: serLabel, type: 'nominal', title: serLabel };
    encoding.tooltip.push({ field: serLabel, type: 'nominal' });
  }

  return {
    data: { values: mapData(data, fields) },
    mark: { type: 'line', point: true },
    encoding,
  };
}

function buildPieSpec(data, fields, dimension, metric, colorMap) {
  const dimLabel = getLabel(fields, dimension);
  const metLabel = getLabel(fields, metric);
  const mappedData = mapData(data, fields);

  const colorEncoding = resolveColorScale(colorMap, dimLabel, data, fields) || {
    field: dimLabel,
    type: 'nominal',
  };

  return {
    data: { values: mappedData },
    transform: [
      // Drop slices with no positive metric value or a missing dimension label — both
      // render as visually empty wedges in Vega-Lite (e.g. a 0% slice with no legend
      // entry from a `null` group key).
      { filter: `datum['${metLabel}'] > 0 && datum['${dimLabel}'] != null && datum['${dimLabel}'] !== ''` },
      { joinaggregate: [{ op: 'sum', field: metLabel, as: '_total' }] },
      { calculate: `datum['${metLabel}'] / datum._total`, as: '_pct' },
      { calculate: `datum._pct >= 0.05 ? datum['${dimLabel}'] : 'Other'`, as: '_category' },
      {
        aggregate: [
          { op: 'sum', field: metLabel, as: metLabel },
          { op: 'sum', field: '_pct', as: '_pct' },
        ],
        groupby: ['_category'],
      },
      { calculate: "datum._pct >= 0.05 ? format(datum._pct, '.1%') : ''", as: '_label' },
    ],
    encoding: {
      theta: { field: metLabel, type: 'quantitative', stack: true },
      order: { field: metLabel, type: 'quantitative', sort: 'descending' },
      color: { ...colorEncoding, field: '_category', title: dimLabel },
    },
    layer: [
      {
        mark: { type: 'arc', outerRadius: 110 },
        encoding: {
          tooltip: [
            { field: '_category', type: 'nominal', title: dimLabel },
            { field: metLabel, type: 'quantitative', format: ',.1f' },
            { field: '_pct', type: 'quantitative', format: '.1%', title: 'Share' },
          ],
        },
      },
      {
        mark: { type: 'text', radius: 75, fontSize: 11 },
        encoding: {
          theta: { field: metLabel, type: 'quantitative', stack: true },
          order: { field: metLabel, sort: 'descending' },
          text: { field: '_label' },
          color: { value: '#fff' },
        },
      },
    ],
  };
}

function buildStackedBarSpec(data, fields, dimension, metric, series, colorMap) {
  const dimLabel = getLabel(fields, dimension);
  const metLabel = getLabel(fields, metric);
  const serLabel = getLabel(fields, series);
  const metFormat = getAxisFormat(fields, metric);
  const colorEncoding = resolveColorScale(colorMap, serLabel, data, fields) || {
    field: serLabel,
    type: 'nominal',
    title: serLabel,
  };

  return {
    data: { values: mapData(data, fields) },
    mark: 'bar',
    encoding: {
      x: { field: dimLabel, type: 'nominal', title: dimLabel, axis: { labelAngle: -45 } },
      y: {
        field: metLabel,
        type: 'quantitative',
        title: metLabel,
        stack: true,
        ...(metFormat ? { axis: { format: metFormat } } : {}),
      },
      color: colorEncoding,
      tooltip: [
        { field: dimLabel, type: 'nominal' },
        { field: serLabel, type: 'nominal' },
        { field: metLabel, type: 'quantitative', ...(metFormat ? { format: metFormat } : {}) },
      ],
    },
  };
}

function buildGroupedBarSpec(data, fields, dimension, metric, series, colorMap) {
  const dimLabel = getLabel(fields, dimension);
  const metLabel = getLabel(fields, metric);
  const serLabel = getLabel(fields, series);
  const metFormat = getAxisFormat(fields, metric);
  const colorEncoding = resolveColorScale(colorMap, serLabel, data, fields) || {
    field: serLabel,
    type: 'nominal',
    title: serLabel,
  };

  return {
    data: { values: mapData(data, fields) },
    mark: 'bar',
    encoding: {
      x: { field: dimLabel, type: 'nominal', title: dimLabel },
      xOffset: { field: serLabel },
      y: {
        field: metLabel,
        type: 'quantitative',
        title: metLabel,
        ...(metFormat ? { axis: { format: metFormat } } : {}),
      },
      color: colorEncoding,
      tooltip: [
        { field: dimLabel, type: 'nominal' },
        { field: serLabel, type: 'nominal' },
        { field: metLabel, type: 'quantitative', ...(metFormat ? { format: metFormat } : {}) },
      ],
    },
  };
}

function buildVegaSpec(chartType, data, fields, dimension, metric, series, colorMap) {
  switch (chartType) {
    case 'bar':
      return buildBarSpec(data, fields, dimension, metric, colorMap);
    case 'line':
      return buildLineSpec(data, fields, dimension, metric, series, colorMap);
    case 'pie':
      return buildPieSpec(data, fields, dimension, metric, colorMap);
    case 'stacked-bar':
      return buildStackedBarSpec(data, fields, dimension, metric, series, colorMap);
    case 'grouped-bar':
      return buildGroupedBarSpec(data, fields, dimension, metric, series, colorMap);
    default:
      return buildBarSpec(data, fields, dimension, metric, colorMap);
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AssistantChart({ chartType, data, fields, dimension, metric, series, colors: colorMap, filename = 'chart' }) {
  const chartRef = useRef(null);
  const viewRef = useRef(null);
  const hasRenderedRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (hasRenderedRef.current) return;
    if (!chartRef.current || !data || !fields || !chartType) return;

    // Exclude totals rows — they distort chart rendering
    const chartData = data.filter((row) => row.rowType !== 'total');

    const spec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      width: 680,
      height: 300,
      autosize: { type: 'fit', contains: 'padding' },
      ...buildVegaSpec(chartType, chartData, fields, dimension, metric, series, colorMap),
      config: defaultConfig,
    };

    embed(chartRef.current, spec, {
      actions: false,
      renderer: 'svg',
      ast: true,
      expr: expressionInterpreter,
    })
      .then((result) => {
        viewRef.current = result.view;
        hasRenderedRef.current = true;
        setStatus('rendered');
      })
      .catch((err) => {
        setErrorMessage(`Chart rendering error: ${err.message}`);
        setStatus('error');
      });

    return () => {
      if (viewRef.current && !hasRenderedRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
  }, [chartType, data, fields, dimension, metric, series, colorMap]);

  const handleSavePng = useCallback(async () => {
    if (!viewRef.current) return;
    const url = await viewRef.current.toImageURL('png', 2);
    const response = await fetch(url);
    const blob = await response.blob();
    downloadBlob(blob, `${filename}.png`);
  }, [filename]);

  const handleSaveSvg = useCallback(async () => {
    if (!viewRef.current) return;
    const svgString = await viewRef.current.toSVG();
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    downloadBlob(blob, `${filename}.svg`);
  }, [filename]);

  if (status === 'error') {
    return <S.Error>{errorMessage}</S.Error>;
  }

  return (
    <>
      <S.Container>
        <div ref={chartRef} style={{ visibility: status === 'loading' ? 'hidden' : 'visible' }} />
      </S.Container>
      {status === 'rendered' && (
        <S.ButtonRow>
          <S.ActionButton onClick={handleSavePng}>Save PNG</S.ActionButton>
          <S.ActionButton onClick={handleSaveSvg}>Save SVG</S.ActionButton>
        </S.ButtonRow>
      )}
    </>
  );
}

export default memo(AssistantChart);
